import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, 
  CheckCircle2, 
  XCircle, 
  Trash2, 
  Loader2, 
  AlertCircle,
  MessageSquare,
  User,
  Calendar,
  Image as ImageIcon
} from 'lucide-react';

const AdminReviews = () => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('ALL');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('reviews')
        .select(`
          *
        `)
        .order('created_at', { ascending: false });
      
      const { data, error } = await query;
      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      // Security check: Only admins can update status via RLS
      const { error } = await supabase
        .from('reviews')
        .update({ status })
        .eq('id', id);
      
      if (error) throw error;
      setReviews(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      setMessage({ type: 'success', text: `Review ${status.toLowerCase()}ed` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const deleteReview = async (id: string) => {
    if (!confirm('Are you sure you want to delete this review?')) return;
    try {
      const { error } = await supabase.from('reviews').delete().eq('id', id);
      if (error) throw error;
      setReviews(prev => prev.filter(r => r.id !== id));
      setMessage({ type: 'success', text: 'Review deleted' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const filteredReviews = filter === 'ALL' 
    ? reviews 
    : reviews.filter(r => r.status.toUpperCase() === filter);

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-emerald" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-serif text-emerald">Guest Reviews</h1>
            <p className="text-[#59635C] font-clean mt-2">Moderate and manage guest feedback.</p>
          </div>
          <div className="flex bg-white rounded-full p-1 border border-accent/10 shadow-sm">
            {['ALL', 'PENDING', 'APPROVED', 'REJECTED'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f as any)}
                className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${
                  filter === f ? 'bg-emerald text-white' : 'text-emerald hover:bg-emerald/5'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-clean font-medium">{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6">
          <AnimatePresence>
            {filteredReviews.map((review) => (
              <motion.div
                key={review.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-6 rounded-2xl border border-accent/10 shadow-sm"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-grow space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center text-emerald">
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-serif text-emerald text-lg">{review.guest_name || review.name}</h3>
                          <div className="flex items-center gap-2 text-[10px] text-accent font-bold uppercase tracking-widest">
                            <Calendar className="w-3 h-3" />
                            {new Date(review.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                        ))}
                      </div>
                    </div>

                    <div className="bg-[#F5F1E8]/30 p-4 rounded-xl">
                      <MessageSquare className="w-4 h-4 text-accent/40 mb-2" />
                      <p className="font-clean text-[#59635C] leading-relaxed italic">"{review.review_text || review.text}"</p>
                    </div>

                    {review.photos && review.photos.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {review.photos.map((photo: string, idx: number) => (
                          <img 
                            key={idx} 
                            src={photo} 
                            alt="Review photo" 
                            className="w-20 h-20 object-cover rounded-lg border border-accent/10 shrink-0"
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex md:flex-col gap-2 shrink-0 justify-end md:justify-start">
                    {review.status === 'pending' && (
                      <button 
                        onClick={() => updateStatus(review.id, 'approved')}
                        className="flex-grow flex items-center justify-center gap-2 px-4 py-2 bg-emerald text-white rounded-xl font-clean font-bold text-xs uppercase tracking-widest hover:bg-emerald/90 transition-all"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                    )}
                    {review.status === 'pending' && (
                      <button 
                        onClick={() => updateStatus(review.id, 'rejected')}
                        className="flex-grow flex items-center justify-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-xl font-clean font-bold text-xs uppercase tracking-widest hover:bg-red-50 transition-all"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    )}
                    <button 
                      onClick={() => deleteReview(review.id)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                      title="Delete Permanently"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredReviews.length === 0 && (
            <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-accent/20">
              <Star className="w-12 h-12 text-accent/30 mx-auto mb-4" />
              <p className="text-[#59635C] font-clean">No reviews found in this category.</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminReviews;
